import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';

import CategoryMegaMenu from './CategoryMegaMenu';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';
import {
  TopbarIconAccountSettings,
  TopbarIconDashboard,
  TopbarIconInbox,
  TopbarIconListings,
  TopbarIconLogin,
  TopbarIconLogout,
  TopbarIconProfileSettings,
  TopbarIconSearch,
} from '../TopbarLinkIcons';

import wedlyLogo from '../../../../assets/wedly-logo.png';
import css from './TopbarDesktop.module.css';

/** AI sparkle icon */
const AISparkleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M8 1L9.2 5.8L14 7L9.2 8.2L8 13L6.8 8.2L2 7L6.8 5.8L8 1Z" fill="currentColor" />
    <path
      d="M13 1L13.6 3.4L16 4L13.6 4.6L13 7L12.4 4.6L10 4L12.4 3.4L13 1Z"
      fill="currentColor"
      opacity="0.65"
    />
    <path
      d="M3 10L3.4 11.6L5 12L3.4 12.4L3 14L2.6 12.4L1 12L2.6 11.6L3 10Z"
      fill="currentColor"
      opacity="0.5"
    />
  </svg>
);

/* ─── Announcement bar ────────────────────────────────────────────────────── */

const LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'es', label: 'Español' },
];

const CURRENCY_OPTIONS = [
  { code: 'AUD', label: 'A$ AUD' },
  { code: 'USD', label: '$ USD' },
  { code: 'EUR', label: '\u20AC EUR' },
  { code: 'GBP', label: '\u00A3 GBP' },
];

const AnnouncementBar = props => {
  const { selectedLanguage, selectedCurrency, onLanguageChange, onCurrencyChange } = props;
  const selectedLanguageLabel =
    LANGUAGE_OPTIONS.find(option => option.code === selectedLanguage)?.label || 'English';

  return (
    <div className={css.announcementBar}>
      <div className={css.announcementFiller} />
      <span className={css.announcementTagline}>
        <FormattedMessage id="WedlyPage.announcement" />
      </span>
      <div className={css.announcementSelectors}>
        <Menu skipFocusOnNavigation={true}>
          <MenuLabel className={css.announcementBtn}>
            {selectedLanguageLabel} &#8964;
          </MenuLabel>
          <MenuContent className={css.announcementMenuContent}>
            {LANGUAGE_OPTIONS.map(option => (
              <MenuItem key={option.code} className={css.announcementMenuItem}>
                <InlineTextButton
                  rootClassName={css.announcementMenuButton}
                  onClick={() => onLanguageChange(option.code)}
                >
                  {option.label}
                </InlineTextButton>
              </MenuItem>
            ))}
          </MenuContent>
        </Menu>
        <Menu skipFocusOnNavigation={true}>
          <MenuLabel className={css.announcementBtn}>
            {selectedCurrency} &#8964;
          </MenuLabel>
          <MenuContent className={css.announcementMenuContent}>
            {CURRENCY_OPTIONS.map(option => (
              <MenuItem key={option.code} className={css.announcementMenuItem}>
                <InlineTextButton
                  rootClassName={css.announcementMenuButton}
                  onClick={() => onCurrencyChange(option.code)}
                >
                  {option.label}
                </InlineTextButton>
              </MenuItem>
            ))}
          </MenuContent>
        </Menu>
      </div>
    </div>
  );
};

/* ─── Centred logo ────────────────────────────────────────────────────────── */

const WedlyLogo = () => (
  <div className={css.logoBlock}>
    <NamedLink
      name="LandingPage"
      className={css.logoLink}
      aria-label="Wedly — The Wedding Marketplace"
    >
      <img className={css.logoMark} src={wedlyLogo} alt="" aria-hidden="true" />
      <span className={css.logoName}>WEDLY</span>
      <span className={css.logoSub}>
        <FormattedMessage id="TopbarDesktop.logoTagline" />
      </span>
    </NamedLink>
  </div>
);

/* ─── Auth sub-components ─────────────────────────────────────────────────── */

const DashboardLink = ({ routeName }) => (
  <NamedLink
    id="dashboard-link"
    className={css.iconBtn}
    name={routeName}
    aria-label="Dashboard"
  >
    <TopbarIconDashboard size={24} />
  </NamedLink>
);

const InboxIconLink = ({ notificationCount, inboxTab, intl }) => (
  <NamedLink
    id="inbox-link"
    className={css.iconBtn}
    name="InboxPage"
    params={{ tab: inboxTab }}
    aria-label={intl.formatMessage({ id: 'TopbarDesktop.inbox' })}
  >
    <TopbarIconInbox size={24} />
    {notificationCount > 0 && <span className={css.notificationDot} />}
  </NamedLink>
);

const ProfileMenu = ({ currentPage, currentUser, onLogout, showManageListingsLink, intl }) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  return (
    <Menu className={css.profileMenu} skipFocusOnNavigation={true}>
      <MenuLabel
        id="profile-menu-label"
        className={css.profileMenuLabel}
        isOpenClassName={css.profileMenuIsOpen}
        ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.screenreader.profileMenu' })}
      >
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {showManageListingsLink ? (
          <MenuItem key="ManageListingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <span className={css.menuItemBorder} />
              <span className={css.menuLinkIcon}>
                <TopbarIconListings size={24} />
              </span>
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          </MenuItem>
        ) : null}
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <span className={css.menuLinkIcon}>
              <TopbarIconProfileSettings size={24} />
            </span>
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <span className={css.menuLinkIcon}>
              <TopbarIconAccountSettings size={24} />
            </span>
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <span className={css.menuLinkIcon}>
              <TopbarIconLogout size={24} />
            </span>
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

/* ─── Main component ──────────────────────────────────────────────────────── */

/**
 * Wedly editorial desktop topbar.
 * Structure: [announcement bar] / [nav-left | centred logo | nav-right]
 * When scrolled, the main header row shows a subtle full-bleed wedding-motif SVG (see `TopbarDesktop.module.css`).
 *
 * @component
 * @param {Object} props
 * @param {string?}  props.className
 * @param {string?}  props.rootClassName
 * @param {Object}   props.config
 * @param {Array?}   props.customLinks
 * @param {Object}   props.currentUser
 * @param {string?}  props.currentPage
 * @param {boolean}  props.isAuthenticated
 * @param {number}   props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object}   props.intl
 * @param {boolean}  props.showCreateListingsLink
 * @param {boolean}  props.showSellerDashboardLink
 * @param {string}   props.inboxTab
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    showCreateListingsLink,
    showSellerDashboardLink = false,
    showCustomerDashboardLink = false,
    dashboardRouteName = null,
    inboxTab,
  } = props;

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;
  const classes = classNames(rootClassName || css.root, className, {
    [css.scrolled]: isScrolled,
  });

  const dashboardLinkMaybe =
    authenticatedOnClientSide && dashboardRouteName ? (
      <DashboardLink routeName={dashboardRouteName} />
    ) : null;

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxIconLink notificationCount={notificationCount} inboxTab={inboxTab} intl={intl} />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      showManageListingsLink={showCreateListingsLink}
      intl={intl}
    />
  ) : null;

  const authIconsMaybe = !isAuthenticatedOrJustHydrated ? (
    <NamedLink
      id="login-link"
      name="LoginPage"
      className={css.iconBtn}
      aria-label={intl.formatMessage({ id: 'TopbarDesktop.login' })}
    >
      <TopbarIconLogin size={24} />
    </NamedLink>
  ) : null;

  const initialLanguageFromIntl = intl?.locale?.split('-')?.[0] || 'en';
  const [selectedLanguage, setSelectedLanguage] = useState(initialLanguageFromIntl);
  const [selectedCurrency, setSelectedCurrency] = useState(config?.currency || 'AUD');

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const savedLanguage = window.localStorage.getItem('wedly:selectedLanguage');
    const savedCurrency = window.localStorage.getItem('wedly:selectedCurrency');

    if (savedLanguage) {
      setSelectedLanguage(savedLanguage);
    }
    if (savedCurrency) {
      setSelectedCurrency(savedCurrency);
    }
  }, []);

  const handleLanguageChange = language => {
    setSelectedLanguage(language);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('wedly:selectedLanguage', language);
      window.dispatchEvent(new Event('wedly:preferences-updated'));
    }
  };

  const handleCurrencyChange = currency => {
    setSelectedCurrency(currency);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('wedly:selectedCurrency', currency);
      window.dispatchEvent(new Event('wedly:preferences-updated'));
    }
  };

  return (
    <nav
      className={classes}
      aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.topbarNavigation' })}
    >
      {/* ── Announcement strip ── */}
      <AnnouncementBar
        selectedLanguage={selectedLanguage}
        selectedCurrency={selectedCurrency}
        onLanguageChange={handleLanguageChange}
        onCurrencyChange={handleCurrencyChange}
      />

      {/* ── Main header row ── */}
      <div className={css.mainHeader}>
        {/* Left: category mega-menu + custom nav links */}
        <div className={css.navLeft}>
          <CategoryMegaMenu categories={config?.categoryConfiguration?.categories} />
          <CustomLinksMenu
            currentPage={currentPage}
            customLinks={customLinks}
            intl={intl}
            hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
            showCreateListingsLink={showCreateListingsLink}
          />
        </div>

        {/* Centre: typographic logo */}
        <WedlyLogo />

        {/* Right: AI button + search + auth icons */}
        <div className={css.navRight}>
          {/* <NamedLink
            name="AISearchPage"
            className={css.aiBtn}
            aria-label={intl.formatMessage({ id: 'TopbarDesktop.shopWithAI' })}
          >
            <AISparkleIcon />
            <span className={css.aiBtnText}>
              <FormattedMessage id="TopbarDesktop.shopWithAI" />
            </span>
          </NamedLink> */}

          <NamedLink
            name="SearchPage"
            className={css.iconBtn}
            aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.searchIcon' })}
          >
            <TopbarIconSearch size={24} />
          </NamedLink>

          {authIconsMaybe}
          {dashboardLinkMaybe}
          {profileMenuMaybe}
          {inboxLinkMaybe}
        </div>
      </div>
    </nav>
  );
};

export default TopbarDesktop;
